import React, { useMemo } from "react";
import { RobotoTextProps } from "../../@types/Components";
import { useExportContext } from "../../provider/ExportProvider";
import { defaultFontColor } from "../../utils/colors";
import {
  DEFAULT_TEXT_LINE_HEIGHT,
  DEFAULT_ZOOM_FONT_SIZE,
  FONT_SIZE_ZOOM_FACTOR,
  MAX_ZOOM_FONT_SIZE,
  MIN_ZOOM_FONT_SIZE,
  ROBOTO_FONT_PATH,
} from "../../utils/constants";
import { getAdjustedFontSizeForExport } from "../../utils/fontUtils";
import { useZoomChangeContext } from "../../provider/ZoomChangeProvider";
import { Text } from "@react-three/drei";

/**
 * `RobotoText` is a component that renders text in a 3D space using the `@react-three/drei` Text component.
 * This custom component is used to standardize the use of the Roboto font across all text elements in the 3D scene
 * without the need to specify `font="/fonts/Roboto-Regular.ttf"` for each text component individually.
 */
const RobotoText = ({
  name,
  visible = true,
  fontSize = DEFAULT_ZOOM_FONT_SIZE,
  color = defaultFontColor,
  anchorX = "center",
  anchorY = "middle",
  position = undefined,
  message = "",
  textAlign = "justify",
  maxWidth = undefined,
  lineHeight = DEFAULT_TEXT_LINE_HEIGHT,
  font = ROBOTO_FONT_PATH,
  letterSpacing = 0,
  hasTextbox = false,
}: RobotoTextProps) => {
  const { isExporting, exportCameraHeight, exportCameraWidth } =
    useExportContext();
  const { staggeredZoomData } = useZoomChangeContext();

  // Memoize the adjusted font size to avoid unnecessary recalculations.
  const adjustedFontSize = useMemo(() => {
    if (isExporting && !hasTextbox) {
      if (exportCameraHeight !== 0 && exportCameraWidth !== 0) {
        // Scale based on both camera height and width to account for narrow or wide rooms
        return getAdjustedFontSizeForExport(
          exportCameraHeight,
          exportCameraWidth
        );
      }
    } else {
      const tmpFontSize = fontSize * (FONT_SIZE_ZOOM_FACTOR / staggeredZoomData);
      return Math.max(
        Math.min(tmpFontSize, MAX_ZOOM_FONT_SIZE),
        MIN_ZOOM_FONT_SIZE
      );
    }
    return fontSize;
  }, [isExporting, hasTextbox, fontSize, exportCameraHeight, exportCameraWidth, staggeredZoomData]);

  return (
    <Text
      name={name}
      visible={visible}
      font={font}
      fontSize={adjustedFontSize}
      color={color}
      anchorX={anchorX}
      anchorY={anchorY}
      position={position}
      textAlign={textAlign}
      maxWidth={maxWidth}
      lineHeight={lineHeight}
      letterSpacing={letterSpacing}
    >
      {message}
    </Text>
  );
};

export default RobotoText;
